import { Box, Stack, Typography } from "@mui/material";
import { t } from "i18next";
import logoDesktop from "@/assets/logo-desktop.png";

const CELL_COUNT = 4;
const CELL_STAGGER_SECONDS = 0.2;

interface AppLoaderProps {
	"data-testid"?: string;
}

/**
 * Full-screen loader shown while the app starts (Keycloak initialising).
 * A battery whose cells charge one after another; under
 * `prefers-reduced-motion` the cells stay lit instead of animating.
 */
export function AppLoader({ "data-testid": testId }: AppLoaderProps) {
	return (
		<Box
			data-testid={testId}
			role="status"
			aria-live="polite"
			sx={{
				position: "relative",
				height: "100dvh",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				bgcolor: "background.default",
			}}
		>
			<Stack alignItems="center" spacing={2.75}>
				<Box aria-hidden sx={{ display: "flex", alignItems: "center" }}>
					<Box
						sx={{
							width: 100,
							height: 46,
							boxSizing: "border-box",
							border: "2.5px solid",
							borderColor: "primary.main",
							borderRadius: 1.5,
							p: 0.625,
							display: "flex",
							gap: 0.5,
						}}
					>
						{Array.from({ length: CELL_COUNT }, (_, index) => (
							<Box
								key={index}
								sx={{
									flex: 1,
									borderRadius: 0.5,
									bgcolor: "primary.main",
									"@keyframes app-loader-charge": {
										"0%, 8%": { opacity: 0.14 },
										"22%, 82%": { opacity: 1 },
										"96%, 100%": { opacity: 0.14 },
									},
									animation: "app-loader-charge 2.4s ease-in-out infinite",
									animationDelay: `${index * CELL_STAGGER_SECONDS}s`,
									"@media (prefers-reduced-motion: reduce)": {
										animation: "none",
										opacity: 0.9,
									},
								}}
							/>
						))}
					</Box>
					<Box
						sx={{
							width: 5,
							height: 16,
							borderRadius: "0 3px 3px 0",
							bgcolor: "primary.main",
						}}
					/>
				</Box>
				<Stack alignItems="center" spacing={0.5}>
					<Typography variant="subtitle1" fontWeight={600}>
						{t("Loading")}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{t("This only takes a moment.")}
					</Typography>
				</Stack>
			</Stack>
			<Box
				component="img"
				src={logoDesktop}
				alt=""
				sx={{ position: "absolute", bottom: 28, height: 22, width: "auto" }}
			/>
		</Box>
	);
}
